import PropTypes from 'prop-types';

const ScoreBreakdown = ({ score, quote, onClose }) => {
  if (!score) {
    return null;
  }

  const formatCost = (cost) => {
    return cost ? `$${cost.toLocaleString()}` : 'N/A';
  };

  const getScoreColor = (value) => {
    if (value >= 80) return 'excellent';
    if (value >= 60) return 'good';
    if (value >= 40) return 'fair';
    return 'poor';
  };

  const scoreMetrics = [
    {
      name: 'Cost Score',
      value: score.costScore,
      color: 'cost',
      description: 'Lower cost = higher score'
    },
    {
      name: 'Speed Score',
      value: score.speedScore,
      color: 'speed',
      description: 'Faster transit = higher score'
    },
    {
      name: 'Reliability Score',
      value: score.reliabilityScore,
      color: 'reliability',
      description: 'Provider track record'
    }
  ];

  return (
    <div className="score-breakdown">
      <div className="score-breakdown-header">
        <h3>Score Breakdown - {score.providerName}</h3>
        <button onClick={onClose} className="btn-close" aria-label="Close">
          ×
        </button>
      </div>

      <div className="score-breakdown-body">
        {/* Total Score */}
        <div className="total-score-display">
          <div className="total-score-value">{score.totalScore}</div>
          <div className="total-score-label">Total Score (out of 100)</div>
          <div className={`score-rating rating-${getScoreColor(score.totalScore)}`}>
            {getScoreColor(score.totalScore).toUpperCase()}
          </div>
        </div>

        {/* Individual Score Bars */}
        <div className="score-metrics">
          {scoreMetrics.map((metric) => (
            <div key={metric.name} className="score-metric">
              <div className="score-metric-header">
                <span className="score-metric-name">{metric.name}</span>
                <span className="score-metric-value">
                  {metric.value?.toFixed(1) || '0.0'} / 100
                </span>
              </div>
              <div className="score-bar-track">
                <div
                  className={`score-bar-fill fill-${metric.color}`}
                  style={{ width: `${metric.value || 0}%` }}
                />
              </div>
              <div className="score-metric-description">{metric.description}</div>
            </div>
          ))}
        </div>

        {/* Quote Details */}
        {quote && (
          <div className="quote-details">
            <h4>Quote Details</h4>
            <div className="details-grid">
              <div className="detail-item">
                <label>Total Cost</label>
                <strong>{formatCost(quote.totalCost)}</strong>
              </div>
              {quote.oceanFreight && (
                <div className="detail-item">
                  <label>Ocean Freight</label>
                  <span>{formatCost(quote.oceanFreight)}</span>
                </div>
              )}
              {quote.fuelSurcharge && (
                <div className="detail-item">
                  <label>Fuel Surcharge</label>
                  <span>{formatCost(quote.fuelSurcharge)}</span>
                </div>
              )}
              {quote.originCharges && (
                <div className="detail-item">
                  <label>Origin Charges</label>
                  <span>{formatCost(quote.originCharges)}</span>
                </div>
              )}
              {quote.destinationCharges && (
                <div className="detail-item">
                  <label>Destination Charges</label>
                  <span>{formatCost(quote.destinationCharges)}</span>
                </div>
              )}
              <div className="detail-item">
                <label>Transit Time</label>
                <strong>
                  {quote.transitDays ? `${quote.transitDays} days` : 'TBD'}
                </strong>
              </div>
              {quote.validUntil && (
                <div className="detail-item">
                  <label>Valid Until</label>
                  <span>{new Date(quote.validUntil).toLocaleDateString()}</span>
                </div>
              )}
              {quote.departureDate && (
                <div className="detail-item">
                  <label>Estimated Departure</label>
                  <span>{new Date(quote.departureDate).toLocaleDateString()}</span>
                </div>
              )}
              {quote.arrivalDate && (
                <div className="detail-item">
                  <label>Estimated Arrival</label>
                  <span>{new Date(quote.arrivalDate).toLocaleDateString()}</span>
                </div>
              )}
              {quote.vesselName && (
                <div className="detail-item">
                  <label>Vessel</label>
                  <span>{quote.vesselName}</span>
                </div>
              )}
              {quote.allInConfirmed && (
                <div className="detail-item detail-item-full">
                  <span className="badge-success">✓ All-in pricing confirmed</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

ScoreBreakdown.propTypes = {
  score: PropTypes.shape({
    quoteId: PropTypes.string.isRequired,
    providerName: PropTypes.string.isRequired,
    totalScore: PropTypes.number.isRequired,
    costScore: PropTypes.number.isRequired,
    speedScore: PropTypes.number.isRequired,
    reliabilityScore: PropTypes.number.isRequired
  }).isRequired,
  quote: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

export default ScoreBreakdown;
